import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Error Codes Reference
 */
const ErrorCodesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const errorCategories = [
    {
      category: "Authentication Errors (1xxx)",
      errors: [
        { code: "1001", message: "Invalid credentials", description: "Username or password is incorrect. Verify your login information and try again.", severity: "error" },
        { code: "1002", message: "Session expired", description: "Your session has timed out due to inactivity. Please log in again.", severity: "warning" },
        { code: "1003", message: "Account locked", description: "Too many failed login attempts. Contact administrator to unlock.", severity: "error" },
        { code: "1004", message: "MFA required", description: "Multi-factor authentication is required. Enter your verification code.", severity: "info" },
        { code: "1005", message: "SSO configuration error", description: "Single Sign-On is misconfigured. Contact your IT administrator.", severity: "error" }
      ]
    },
    {
      category: "Data Errors (2xxx)",
      errors: [
        { code: "2001", message: "Record not found", description: "The requested record does not exist or you don't have access to it.", severity: "warning" },
        { code: "2002", message: "Duplicate record", description: "A record with this identifier already exists in the system.", severity: "error" },
        { code: "2003", message: "Validation failed", description: "The submitted data does not meet validation requirements. Check required fields.", severity: "error" },
        { code: "2004", message: "Data sync pending", description: "Data is being synchronized. Please wait a few minutes and try again.", severity: "info" },
        { code: "2005", message: "Import format error", description: "The imported file format is not supported or contains errors.", severity: "error" }
      ]
    },
    {
      category: "Claims Errors (3xxx)",
      errors: [
        { code: "3001", message: "Missing required field", description: "A required field is empty. Review claim details and complete all mandatory fields.", severity: "error" },
        { code: "3002", message: "Invalid NPI", description: "The National Provider Identifier is invalid or not enrolled with the payer.", severity: "error" },
        { code: "3003", message: "Clearinghouse rejected", description: "The clearinghouse rejected the claim. Check the rejection reason in the claim details.", severity: "error" },
        { code: "3004", message: "Payer not found", description: "The payer ID is not recognized. Verify payer enrollment and ID.", severity: "error" },
        { code: "3005", message: "Timely filing exceeded", description: "The claim is past the payer's timely filing deadline.", severity: "warning" }
      ]
    },
    {
      category: "Integration Errors (4xxx)",
      errors: [
        { code: "4001", message: "Connection failed", description: "Unable to connect to the external system. Check network connectivity.", severity: "error" },
        { code: "4002", message: "Authentication failed", description: "Integration credentials are invalid or expired. Re-authenticate the connection.", severity: "error" },
        { code: "4003", message: "API rate limit exceeded", description: "Too many requests to the external API. Please wait and retry.", severity: "warning" },
        { code: "4004", message: "Data mapping error", description: "Unable to map data between systems. Check field mapping configuration.", severity: "error" },
        { code: "4005", message: "Timeout", description: "The external system did not respond in time. Retry or contact support.", severity: "warning" }
      ]
    },
    {
      category: "System Errors (5xxx)",
      errors: [
        { code: "5001", message: "Internal server error", description: "An unexpected error occurred. If this persists, contact support with the error code.", severity: "error" },
        { code: "5002", message: "Service unavailable", description: "The service is temporarily unavailable. Please try again later.", severity: "warning" },
        { code: "5003", message: "Maintenance mode", description: "The system is under maintenance. Service will resume shortly.", severity: "info" },
        { code: "5004", message: "Feature not available", description: "This feature is not available in your current plan. Contact sales to upgrade.", severity: "info" },
        { code: "5005", message: "Resource limit exceeded", description: "You have reached a system limit. Contact support for assistance.", severity: "warning" }
      ]
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const filteredCategories = errorCategories.map(category => ({
    ...category,
    errors: category.errors.filter(error =>
      error.code.includes(searchTerm) ||
      error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.errors.length > 0);

  return (
    <div className="error-codes-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Error Codes Reference</h1>
            <p className="text-xl text-gray-200">
              Look up error codes and find solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <input
              type="text"
              placeholder="Search by error code or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#0E3056] dark:focus:ring-[#FFC000] focus:border-transparent"
            />
          </div>
        </div>
      </section>

      {/* Error Codes */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-300">No error codes found matching your search.</p>
              </div>
            ) : (
              filteredCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    {category.category}
                  </h2>
                  <div className="space-y-4">
                    {category.errors.map((error, errorIndex) => (
                      <div
                        key={errorIndex}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <span className="font-mono font-bold text-[#0E3056] dark:text-[#FFC000] text-lg">
                              {error.code}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {error.message}
                              </h3>
                              <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(error.severity)}`}>
                                {error.severity}
                              </span>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300">
                              {error.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Need More Help */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
            Can't Find Your Error?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            If you're experiencing an error not listed here, contact our support team with the error code and a description of what you were doing.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#0E3056] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#1a4a7a] transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ErrorCodesPage;
